import React from 'react';
import { motion } from 'framer-motion';
import { Clock, XCircle, CheckCircle2, AlertCircle } from 'lucide-react';
export const SalesLetter = () => {
  return <section className="py-16 px-6 bg-white" data-id="element-135">
      <div className="max-w-3xl mx-auto space-y-12" data-id="element-136">
        {/* Opening Hook */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="prose prose-lg text-brand-charcoal" data-id="element-137">
          <p className="text-xl leading-relaxed font-medium" data-id="element-138">
            <span className="font-bold text-brand-navy" data-id="element-139">Son las 3:15 AM.</span>{' '}
            Tus ojos arden, tu espalda duele y el llanto de tu bebé se siente
            como un eco interminable que perfora tu paciencia. Has probado
            mecerlo, darle el pecho, caminar por la habitación durante horas...
            y en cuanto lo apoyas en la cuna, sus ojos se abren de nuevo.
          </p>
          <p className="text-lg leading-relaxed mt-6" data-id="element-140">
            No es que seas una "mala madre". No es que tu hijo sea "rebelde". Es
            que estás atrapada en el ciclo del Cortisol, y nadie te ha enseñado
            cómo apagar el interruptor biológico de la alerta en un bebé de alta
            sensibilidad.
          </p>
        </motion.div>

        {/* The Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-brand-cream p-8 rounded-2xl border border-brand-coral/20" data-id="element-141">
          <h3 className="text-2xl font-bold text-brand-navy mb-6 flex items-center gap-3" data-id="element-142">
            <AlertCircle className="text-brand-coral w-8 h-8" data-id="element-143" />
            El problema con los consejos "promedio"
          </h3>
          <p className="mb-6 text-lg" data-id="element-144">
            La mayoría de los consejos de sueño en internet están diseñados para
            bebés "promedio". Pero tu bebé es diferente.
          </p>
          <ul className="space-y-4" data-id="element-145">
            {['Se sobresalta con el mínimo ruido', 'Lucha contra el pañal', 'Parece sobreestimularse con solo mirar un juguete brillante'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-146">
                <div className="mt-1 bg-brand-coral/10 p-1 rounded-full" data-id="element-147">
                  <div className="w-2 h-2 bg-brand-coral rounded-full" data-id="element-148"></div>
                </div>
                <span className="text-lg" data-id="element-149">{item}</span>
              </li>)}
          </ul>
          <p className="mt-6 text-lg font-medium text-brand-coral" data-id="element-150">
            Para un bebé sensible, los métodos tradicionales de "dejarlo llorar"
            solo empeoran las cosas, elevando sus niveles de estrés y haciendo
            que el sueño profundo sea biológicamente imposible. Estás operando
            en modo supervivencia, con una niebla cerebral que te impide
            disfrutar de la maternidad y que está afectando tu salud y tu
            relación de pareja.
          </p>
        </motion.div>

        {/* Why What You Tried Didn't Work */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-151">
          <h3 className="text-3xl font-bold text-center mb-10" data-id="element-152">
            Por qué lo que has intentado NO funciona
          </h3>
          <div className="flex flex-col gap-8" data-id="element-153">
            <div data-id="element-154">
              <img src="https://cdn.magicpatterns.com/uploads/hUmjEiFqEfyPuKT58gcoxw/Editar2.png" alt="Antes y después: madre agotada a las 3:14 AM vs madre feliz a las 7:00 AM con El Método Sueño Profundo" className="w-full max-w-2xl mx-auto rounded-2xl shadow-lg" data-id="element-155" />
            </div>
            <div className="space-y-6" data-id="element-156">
              {[{
              title: 'Intentar "cansarlo mucho" durante el día',
              desc: 'Esto dispara la adrenalina y el cortisol, causando el famoso "segundo aire" que mantiene al bebé despierto y agitado.'
            }, {
              title: 'Mecerlo hasta que se duerma profundamente',
              desc: 'El bebé asocia tus brazos con la seguridad; cuando despierta en la cuna (un lugar diferente), entra en pánico y reclama tu presencia de inmediato.'
            }, {
              title: 'Seguir horarios rígidos de apps genéricas',
              desc: 'Estas aplicaciones no entienden las ventanas biológicas de 90 minutos de un bebé sensible que vive en un ambiente con estímulos constantes.'
            }].map((item, i) => <div key={i} className="flex gap-4 p-6 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-157">
                  <XCircle className="w-8 h-8 text-brand-coral flex-shrink-0" data-id="element-158" />
                  <div data-id="element-159">
                    <h4 className="font-bold text-xl mb-2 text-brand-navy" data-id="element-160">
                      {item.title}
                    </h4>
                    <p className="text-brand-charcoal/80" data-id="element-161">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* The Unique Mechanism */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-brand-navy text-white p-8 md:p-10 rounded-3xl shadow-xl relative overflow-hidden" data-id="element-162">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-amber/20 rounded-full blur-3xl" data-id="element-163"></div>
          <div className="relative z-10" data-id="element-164">
            <div className="inline-flex items-center gap-2 bg-brand-amber/20 text-brand-amber px-4 py-2 rounded-full text-sm font-bold mb-6" data-id="element-165">
              <CheckCircle2 className="w-4 h-4" data-id="element-166" /> LA SOLUCIÓN
            </div>
            <h3 className="text-3xl font-bold mb-6 text-white" data-id="element-167">
              Sincronización Neuro-Sensorial (SNS)
            </h3>
            <p className="text-lg leading-relaxed text-white/90 mb-6" data-id="element-168">
              A diferencia de los entrenamientos de sueño agresivos, el método
              SNS trabaja CON la biología de tu hijo. Identificamos el momento
              exacto en que la melatonina está en su pico más alto y
              transformamos su habitación en una "Cueva del Sueño" que anula los
              micro-despertares.
            </p>
            <p className="text-xl font-medium text-brand-amber bg-brand-amber/10 p-4 rounded-xl border border-brand-amber/20" data-id="element-169">
              No ignoramos el llanto; eliminamos el estímulo que lo provoca.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
};